// components/portal/UnitMiniPlan.tsx
'use client';

import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ExternalLink, Map as MapIcon } from 'lucide-react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { BrandMark } from '@/components/ui/BrandMark';
import { canAccessFootfall } from '#lib/access/footfallAccess';
import { levelFeatures, ringCentre, unitName, venueBounds, venuePlaces, type PulsePlace } from '#lib/footfall/pulse';
import { neighbouringUnits, readPolygon } from '#lib/leasing/planGeometry';
import { locateUnitOnVenue, venueLevelForFloor } from '#lib/leasing/unitOnVenue';
import { hydrateCentre, openMerchPlanWindow } from '@/components/footfall/pulseState';
import { TABLE_GLASS_HEADER, TABLE_GLASS_ROW } from './tableSurface';
import type { LeasingDealDTO, LeasingUnitDTO, PropertyVenue } from '#types/types';

const PLAN_HEIGHT = 280;
// Ten rows in view (owner, 2026-10-14: at 200 px three rows of 111 showed and it read as not scrollable).
const ROLL_HEIGHT = 420;
const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN ?? '';
// Plan's own thresholds (components/footfall/PulseEditor.tsx): below this zoom a logo is unreadable.
const LOGO_MIN_ZOOM = 16.2;
const LOGO_SIZE = 24;
const EMPTY = { type: 'FeatureCollection' as const, features: [] };
type FloorInfo = { name?: string; level?: number | null };

/**
 * The centre as Plan shows it, small: the street map, the level's shops coloured by trade, each linked
 * shop's brand logo once zoomed in. Plan's own map style and layers (components/footfall/PulseEditor.tsx),
 * none of its editing. A click on a shop picks it (owner, 2026-10-14: "select the unit directly in the
 * plan; double-clicking should not open Plan mode").
 */
const CentreMap = React.memo(function CentreMap({ venue, places, levelId, focusShopId, onPickShop }: {
  venue: PropertyVenue;
  places: PulsePlace[];
  levelId: string | null;
  /** The picked unit's shop on the map: outlined, and the map flies onto it. */
  focusShopId: string | null;
  onPickShop: (shopId: string) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<import('mapbox-gl').Map | null>(null);
  const [ready, setReady] = useState(false);
  const pickRef = useRef(onPickShop);
  useEffect(() => { pickRef.current = onPickShop; }, [onPickShop]);

  useEffect(() => {
    if (!MAPBOX_TOKEN || !hostRef.current || mapRef.current) return;
    let disposed = false;
    let observer: ResizeObserver | null = null;
    void (async () => {
      const mapboxgl = (await import('mapbox-gl')).default;
      if (disposed || !hostRef.current) return;
      mapboxgl.accessToken = MAPBOX_TOKEN;
      const map = new mapboxgl.Map({ container: hostRef.current, style: 'mapbox://styles/mapbox/dark-v11', center: [2.0438, 48.7829], zoom: 16, attributionControl: false, dragRotate: false, pitchWithRotate: false, touchPitch: false, doubleClickZoom: false, fadeDuration: 0 });
      mapRef.current = map;
      map.on('load', () => {
        map.addSource('venue', { type: 'geojson', data: EMPTY });
        map.addLayer({ id: 'venue-building', type: 'fill', source: 'venue', filter: ['==', ['get', 'kind'], 'building'], paint: { 'fill-color': '#242424', 'fill-outline-color': '#333333' } });
        map.addLayer({ id: 'venue-amenity', type: 'fill', source: 'venue', filter: ['==', ['get', 'kind'], 'amenity'], paint: { 'fill-color': '#2E2E2E', 'fill-outline-color': '#3A3A3A' } });
        map.addLayer({ id: 'venue-unit', type: 'fill', source: 'venue', filter: ['==', ['get', 'kind'], 'unit'], paint: { 'fill-color': ['get', 'colour'], 'fill-opacity': 0.88 } });
        map.addLayer({ id: 'venue-unit-line', type: 'line', source: 'venue', filter: ['==', ['get', 'kind'], 'unit'], paint: { 'line-color': '#000000', 'line-width': 0.8 } });
        map.addLayer({ id: 'venue-selected-fill', type: 'fill', source: 'venue', filter: ['==', ['get', 'id'], ''], paint: { 'fill-color': '#FFFFFF', 'fill-opacity': 0.25 } });
        map.addLayer({ id: 'venue-selected', type: 'line', source: 'venue', filter: ['==', ['get', 'id'], ''], paint: { 'line-color': '#FFFFFF', 'line-width': 3 } });
        map.addLayer({ id: 'venue-label', type: 'symbol', source: 'venue', minzoom: LOGO_MIN_ZOOM, filter: ['all', ['==', ['get', 'kind'], 'unit'], ['==', ['get', 'logo'], 0]], layout: { 'text-field': ['get', 'label'], 'text-size': 10, 'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'], 'text-max-width': 7 }, paint: { 'text-color': '#ECECEC', 'text-halo-color': '#000000', 'text-halo-width': 1.2 } });
        if (!disposed) setReady(true);
      });
      map.on('click', 'venue-unit', (e) => { const id = e.features?.[0]?.properties?.id; if (typeof id === 'string') pickRef.current(id); });
      map.on('mouseenter', 'venue-unit', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'venue-unit', () => { map.getCanvas().style.cursor = ''; });
      observer = new ResizeObserver(() => map.resize());
      observer.observe(hostRef.current);
    })();
    return () => { disposed = true; observer?.disconnect(); mapRef.current?.remove(); mapRef.current = null; };
  }, []);

  // The level's shops, framed on the level.
  const features = useMemo(() => levelFeatures(venue, places, levelId, 'mix', []), [venue, places, levelId]);
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    (map.getSource('venue') as import('mapbox-gl').GeoJSONSource | undefined)?.setData(features);
    const bounds = venueBounds(venue, levelId);
    if (bounds && !focusShopId) map.fitBounds(bounds, { padding: 20, duration: 0 });
    // focusShopId is read, not watched: the focus effect below does the flying.
  }, [ready, features, venue, levelId]);
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    for (const layer of ['venue-selected', 'venue-selected-fill']) map.setFilter(layer, ['==', ['get', 'id'], focusShopId ?? '']);
    const shop = focusShopId ? venue.features.find((f) => f.id === focusShopId) : null;
    if (!shop) return;
    const points = shop.rings.flat();
    const [w, s, e, n] = [Math.min(...points.map((p) => p[0])), Math.min(...points.map((p) => p[1])), Math.max(...points.map((p) => p[0])), Math.max(...points.map((p) => p[1]))];
    // Centred on the shop, the shops around it in view and close enough to carry their logos and names.
    const camera = map.cameraForBounds([[w, s], [e, n]], { padding: 60 });
    map.easeTo({ center: [(w + e) / 2, (s + n) / 2], zoom: Math.min(camera?.zoom ?? 17.4, 17.4), duration: 600 });
  }, [ready, focusShopId, venue]);

  // Each linked shop's logo, as in Plan, shown once a shop is big enough on screen to carry it.
  const logos = useMemo(() => places.filter((p) => p.feature.level === levelId && p.logoUrl).map((p) => ({
    id: p.feature.id, logoUrl: p.logoUrl!, name: p.name ?? '', unit: unitName(p.feature), at: ringCentre([p.feature.rings.reduce((a, b) => (b.length > a.length ? b : a))]),
  })), [places, levelId]);
  const logoKey = logos.map((l) => `${l.id}:${l.logoUrl}`).join('|');
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    let cancelled = false;
    const made: Array<{ marker: import('mapbox-gl').Marker; root: ReturnType<typeof createRoot>; el: HTMLElement }> = [];
    const toggle = () => { const on = map.getZoom() >= LOGO_MIN_ZOOM; for (const m of made) m.el.style.visibility = on ? '' : 'hidden'; };
    void import('mapbox-gl').then(({ default: mapboxgl }) => {
      if (cancelled) return;
      for (const logo of logos) {
        // The logo, and under it the brand's name and the shop's number (owner, 2026-10-13: zoomed in,
        // the name of the brand must read), as Plan captions its logos.
        const el = document.createElement('div');
        Object.assign(el.style, { pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center' });
        const tile = el.appendChild(document.createElement('div'));
        const caption = el.appendChild(document.createElement('div'));
        caption.textContent = logo.unit ? `${logo.name} · ${logo.unit}` : logo.name;
        Object.assign(caption.style, { marginTop: '2px', maxWidth: '110px', overflow: 'hidden', textOverflow: 'ellipsis', padding: '0 4px', borderRadius: '4px', background: 'rgba(27,27,27,0.85)', color: '#ECECEC', fontSize: '10px', lineHeight: '14px', whiteSpace: 'nowrap' });
        const root = createRoot(tile);
        root.render(<BrandMark logoUrl={logo.logoUrl} name={logo.name} size={LOGO_SIZE} radius={6} raised />);
        made.push({ marker: new mapboxgl.Marker({ element: el, anchor: 'top', offset: [0, -LOGO_SIZE / 2] }).setLngLat(logo.at).addTo(map), root, el });
      }
      toggle();
    });
    map.on('zoom', toggle);
    return () => {
      cancelled = true;
      map.off('zoom', toggle);
      for (const m of made) { m.marker.remove(); setTimeout(() => m.root.unmount(), 0); }
    };
    // logoKey stands for logos: the markers are rebuilt only when the logos on the level change.
  }, [ready, logoKey]);

  if (!MAPBOX_TOKEN) return <div className="flex h-full items-center justify-center text-[#8E8E8E]" style={{ fontSize: 13 }}>The map is not configured on this environment.</div>;
  // No Mapbox wordmark, as on Plan's and the brand's maps.
  return <div ref={hostRef} className="h-full w-full [&_.mapboxgl-ctrl-logo]:!hidden" />;
});

/**
 * The asset at a glance in the Launch bar (owner, 2026-10-13: "a mini merged plan and a mini rent roll,
 * seen in the blink of an eye ... the merged plan since the start ... with its map context and the
 * brands' logos"). From the moment an asset is picked: the centre as Plan shows it (CentreMap) and the
 * rent roll of the level in view. Once a unit is picked, the plan switches to the unit's floor drawing,
 * centred on it with its neighbours lit, and the rent roll narrows to the unit and the shops around it.
 * The centre's map cannot locate one of our units (the landlord's codes and ours differ), hence the two
 * views and a switch. A click on a shop of the map picks its unit; "Merch plan" opens the asset's plan in its own window.
 */
export const UnitMiniPlan = React.memo(function UnitMiniPlan({ propertyId, unitId, onSelectUnit, lang }: {
  propertyId: string;
  unitId: string | null;
  onSelectUnit: (unitId: string) => void;
  lang: 'en' | 'fr';
}) {
  const { kernel } = useKernel();
  const fr = lang === 'fr';
  const units = useKernelStore((state) => state.inventory.Unit.registry) as Record<string, LeasingUnitDTO | undefined>;
  const floors = useKernelStore((state) => state.inventory.Floor.registry) as Record<string, FloorInfo | undefined>;
  const deals = useKernelStore((state) => state.inventory.Deal.registry) as Record<string, LeasingDealDTO | undefined>;
  const property = useKernelStore((state) => state.inventory.Property.registry[propertyId]);
  const brands = useKernelStore((state) => state.inventory.Brand.registry);
  const userContext = useKernelStore((state) => state.userContext);
  const venue = property?.venue ?? null;

  // The centre's plan and its tenants' brands, as Plan loads them.
  useEffect(() => {
    if (kernel.current) void hydrateCentre(kernel.current, propertyId);
  }, [kernel, propertyId]);

  const onAsset = useMemo(() => Object.values(units).filter((u): u is LeasingUnitDTO => Boolean(u) && u!.propertyId === propertyId), [units, propertyId]);
  const floorIds = useMemo(() => [...new Set(onAsset.flatMap((u) => u.floorIds ?? []))], [onAsset]);
  const floorIdsKey = floorIds.join(',');
  const unit = unitId ? units[unitId] : undefined;

  const places = useMemo(() => (venue ? venuePlaces(venue, brands as never, property?.footfall?.shops ?? []) : []), [venue, brands, property?.footfall?.shops]);
  // The map's shops with the names our tenants are matched on: the brand's, else the centre's label.
  const shops = useMemo(() => places.map((p) => ({ id: p.feature.id, name: p.name ?? p.feature.name, rings: p.feature.rings, level: p.feature.level })), [places]);
  const drawnOn = useCallback((id: string) => onAsset.filter((u) => u.floorIds?.includes(id)).map((u) => ({ id: u.id, tenantName: u.tenantName ?? null, geoJson: u.positions?.find((p) => p.floorId === id)?.geoJson ?? null })), [onAsset]);
  // Each floor of ours is the map level it shares the most brands with (the numbering differs).
  const levelOfFloor = useMemo(() => Object.fromEntries(floorIds.map((id) => [id, venueLevelForFloor(drawnOn(id), shops)])), [floorIds, drawnOn, shops]);
  const levels = useMemo(() => (venue?.levels ?? []).slice().sort((a, b) => a.ordinal - b.ordinal), [venue]);
  const [pickedLevel, setPickedLevel] = useState<string | null>(null);
  const [pickedFor, setPickedFor] = useState(unitId);
  if (pickedFor !== unitId) { setPickedFor(unitId); setPickedLevel(null); }
  const unitFloorId = unit?.floorIds?.[0] ?? null;
  const levelId = pickedLevel ?? (unitFloorId ? levelOfFloor[unitFloorId] : null) ?? levels.find((l) => l.ordinal === 0)?.id ?? levels[0]?.id ?? null;
  const floorId = (pickedLevel === null ? unitFloorId : null) ?? floorIds.find((id) => levelOfFloor[id] === levelId)
    ?? floorIds.map((id) => ({ id, n: onAsset.filter((u) => u.floorIds?.includes(id)).length })).sort((a, b) => b.n - a.n)[0]?.id ?? null;
  const onFloor = useMemo(() => (floorId ? onAsset.filter((u) => u.floorIds?.includes(floorId)) : []), [onAsset, floorId]);
  const idsKey = onAsset.map((u) => u.id).sort().join(',');
  const dealIdsKey = onFloor.map((u) => u.activeDealId).filter((id): id is string => Boolean(id)).sort().join(',');
  // The picked unit on the map: by its brand, else by its outline carried onto the map.
  const located = useMemo(() => (unitId && unitFloorId && levelId ? locateUnitOnVenue(unitId, drawnOn(unitFloorId), shops.filter((x) => x.level === levelId)) : null), [unitId, unitFloorId, levelId, drawnOn, shops]);
  const neighbours = useMemo(() => {
    const drawn = floorId ? drawnOn(floorId) : [];
    const own = drawn.find((u) => u.id === unitId);
    const outline = own ? readPolygon(own.geoJson) : [];
    if (outline.length < 3) return [];
    const near = new Set(neighbouringUnits(outline, drawn.filter((u) => u !== own && readPolygon(u.geoJson).length >= 3)).map((u) => u.id));
    return onFloor.filter((u) => near.has(u.id));
  }, [floorId, drawnOn, unitId, onFloor]);

  // The list carries no outlines and no leases: asked by id, the units route returns the outlines (as
  // the floor page does), and the deals are hydrated the inventory's way.
  useEffect(() => {
    const k = kernel.current;
    if (!k || !idsKey) return;
    const ids = idsKey.split(',');
    void k.queryInventory('Unit', { filters: { ids }, limit: ids.length, force: true });
  }, [kernel, idsKey]);
  useEffect(() => {
    if (floorIdsKey) void kernel.current?.ensureInventoryIds('Floor', floorIdsKey.split(','));
  }, [kernel, floorIdsKey]);
  useEffect(() => {
    if (dealIdsKey) void kernel.current?.ensureInventoryIds('Deal', dealIdsKey.split(','));
  }, [kernel, dealIdsKey]);

  // A shop clicked on the map: the unit of ours the map places there, found the way a picked unit is
  // placed (locateUnitOnVenue), on the floor of ours that is this level.
  const [unplaced, setUnplaced] = useState<{ name: string; forUnit: string | null } | null>(null);
  const pickShop = useCallback((shopId: string) => {
    const floor = floorIds.find((id) => levelOfFloor[id] === levelId);
    const drawn = floor ? drawnOn(floor) : [];
    const onLevel = shops.filter((x) => x.level === levelId);
    const hit = drawn.find((u) => locateUnitOnVenue(u.id, drawn, onLevel)?.shopId === shopId);
    if (hit) { setUnplaced(null); onSelectUnit(hit.id); return; }
    setUnplaced({ name: places.find((p) => p.feature.id === shopId)?.name ?? shops.find((x) => x.id === shopId)?.name ?? '', forUnit: unitId });
  }, [floorIds, levelOfFloor, levelId, drawnOn, shops, places, onSelectUnit, unitId]);

  const mayOpenPlan = canAccessFootfall(userContext);
  // The merch plan in its own window, the launch bar staying where it is (owner, 2026-10-14).
  const openInPlan = useCallback(() => {
    openMerchPlanWindow(propertyId, null);
    kernel.current?.logUserAction('Launch bar merch plan opened', { propertyId, unitId });
  }, [kernel, propertyId, unitId]);

  // The rent roll: the unit and the shops around it (the floor when none is drawn next to it), else the floor.
  const rows = useMemo(() => {
    const byId = (a: LeasingUnitDTO, b: LeasingUnitDTO) => a.unitIdentifier.localeCompare(b.unitIdentifier, 'fr', { numeric: true });
    const around = neighbours.length ? neighbours.slice().sort(byId) : onFloor.filter((u) => u.id !== unitId).sort(byId);
    const list = unit ? [unit, ...around] : onFloor.slice().sort(byId);
    return list.map((u) => {
      const deal = u.activeDealId ? deals[u.activeDealId] : undefined;
      const signed = deal?.status === 'SIGNED' ? deal : undefined;
      return {
        id: u.id, identifier: u.unitIdentifier, own: u.id === unitId,
        tenant: u.tenantName ?? null,
        area: u.areaSqm ? Math.round(u.areaSqm) : null,
        // A placeholder lease records the sitting tenant at a rent of 0: no rent known.
        rent: signed?.rentAmount || null, asking: !u.tenantName ? u.askingRent : null,
        leaseEnd: signed?.leaseEndDate ? signed.leaseEndDate.slice(0, 7) : null,
      };
    });
  }, [unit, neighbours, onFloor, deals, unitId]);

  if (!floorIds.length && !venue) return null;
  const vacant = onFloor.filter((u) => !u.tenantName).length;
  const money = (n: number) => `${Math.round(n).toLocaleString(fr ? 'fr-FR' : 'en-US')} €`;
  const withEnd = rows.some((r) => r.leaseEnd);
  const withRent = rows.some((r) => r.rent || r.asking);
  // Inline, not a class: a class built at run time is one the stylesheet never generated.
  const columns = { gridTemplateColumns: `4rem minmax(0,1fr) 4rem${withRent ? ' 7rem' : ''}${withEnd ? ' 5rem' : ''}` };
  const levelName = (n: number) => (n === 0 ? (fr ? 'RDC' : 'Ground') : `${fr ? 'Niveau' : 'Level'} ${n}`);
  const pill = (active: boolean) => `h-7 rounded-full px-2.5 transition-colors ${active ? 'bg-white/[0.12] text-[#ECECEC]' : 'text-[#AFAFAF] hover:bg-white/[0.08]'}`;
  return (
    <div className="flex flex-col gap-3 pb-1 pt-3" data-testid="unit-mini-plan">
      <div className="relative min-w-0 overflow-hidden rounded-[16px] border border-[#262626] bg-black" style={{ height: PLAN_HEIGHT }}>
        {venue ? (
          <CentreMap venue={venue} places={places} levelId={levelId} focusShopId={located?.shopId ?? null} onPickShop={pickShop} />
        ) : property?.venue === undefined ? <div className="skeleton h-full w-full" aria-busy="true" /> : (
          <div className="flex h-full items-center justify-center text-[#8E8E8E]" style={{ fontSize: 13 }}>{fr ? 'Pas encore de plan pour cet actif' : 'No plan for this asset yet'}</div>
        )}
        {levels.length > 1 ? (
          <div className="absolute left-2 top-2 flex gap-0.5 rounded-full border border-[#262626] bg-[#1B1B1B] p-0.5">
            {levels.map((l) => (
              <button key={l.id} type="button" onClick={() => setPickedLevel(l.id)} className={pill(l.id === levelId)} style={{ fontSize: 12 }}>{levelName(l.ordinal)}</button>
            ))}
          </div>
        ) : null}
        {unplaced && unplaced.forUnit === unitId ? (
          <div className="absolute bottom-2 left-2 rounded-full border border-[#262626] bg-[#1B1B1B] px-3 py-1.5 text-[#AFAFAF]" style={{ fontSize: 12 }}>
            {fr ? `${unplaced.name || 'Ce local'} n’est pas relié à l’état locatif` : `${unplaced.name || 'This shop'} is not linked to the rent roll`}
          </div>
        ) : unit && venue && !located && levelId === (unitFloorId ? levelOfFloor[unitFloorId] : null) ? (
          <div className="absolute bottom-2 left-2 rounded-full border border-[#262626] bg-[#1B1B1B] px-3 py-1.5 text-[#AFAFAF]" style={{ fontSize: 12 }}>
            {fr ? `${unit.unitIdentifier} n’est pas encore placé sur la carte du centre` : `${unit.unitIdentifier} is not placed on the centre's map yet`}
          </div>
        ) : null}
        {mayOpenPlan && venue ? (
          <button type="button" onClick={openInPlan}
            className="absolute bottom-2 right-2 inline-flex h-8 items-center gap-1.5 rounded-full border border-[#262626] bg-[#1B1B1B] px-3 text-[#ECECEC] transition-colors hover:bg-[#262626]" style={{ fontSize: 12 }}>
            <MapIcon size={14} />
            <span style={{ fontSize: 'inherit' }}>{fr ? 'Plan merch' : 'Merch plan'}</span>
            <ExternalLink size={12} className="text-[#8E8E8E]" />
          </button>
        ) : null}
      </div>
      <div className="flex min-w-0 flex-col rounded-[16px] border border-[#262626] bg-[#1B1B1B] p-3" style={{ maxHeight: ROLL_HEIGHT }}>
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <span className="shrink-0 font-semibold text-[#ECECEC]" style={{ fontSize: 14 }}>{unit ? (fr ? 'Le local et ses voisins' : 'The unit and its neighbours') : (fr ? 'État locatif' : 'Rent roll')}</span>
          <span className="truncate text-[#8E8E8E]" style={{ fontSize: 12 }}>
            {[floorId ? floors[floorId]?.name : null, `${onFloor.length} ${fr ? 'locaux' : 'units'}`, `${vacant} ${fr ? 'vacants' : 'vacant'}`].filter(Boolean).join(' · ')}
          </span>
        </div>
        <div className={`grid gap-x-3 border-b px-1 pb-1 text-[#8E8E8E] ${TABLE_GLASS_HEADER}`} style={{ ...columns, fontSize: 12 }}>
          <span style={{ fontSize: 'inherit' }}>{fr ? 'Local' : 'Unit'}</span>
          <span style={{ fontSize: 'inherit' }}>{fr ? 'Preneur' : 'Tenant'}</span>
          <span className="text-right" style={{ fontSize: 'inherit' }}>m²</span>
          {withRent ? <span className="text-right" style={{ fontSize: 'inherit' }}>{fr ? 'Loyer / an' : 'Rent / yr'}</span> : null}
          {withEnd ? <span className="text-right" style={{ fontSize: 'inherit' }}>{fr ? 'Fin bail' : 'Lease end'}</span> : null}
        </div>
        <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto">
          {rows.map((r) => (
            <button key={r.id} type="button" onClick={() => onSelectUnit(r.id)}
              className={`grid w-full items-center gap-x-3 border-b px-1 py-1.5 text-left transition-colors ${TABLE_GLASS_ROW} ${r.own ? '!bg-white/[0.08]' : ''}`}
              style={{ ...columns, fontSize: 13 }}>
              <span className={r.own ? 'text-[#ECECEC]' : 'text-[#AFAFAF]'} style={{ fontSize: 'inherit' }}>{r.identifier}</span>
              <span className={`truncate ${r.tenant ? 'text-[#ECECEC]' : 'text-[#8E8E8E]'}`} style={{ fontSize: 'inherit' }}>{r.tenant ?? 'Vacant'}</span>
              <span className="text-right font-mono text-[#AFAFAF]" style={{ fontSize: 'inherit' }}>{r.area ?? '-'}</span>
              {withRent ? <span className="text-right font-mono text-[#AFAFAF]" style={{ fontSize: 'inherit' }}>{r.rent ? money(r.rent) : r.asking ? `${money(r.asking)}*` : '-'}</span> : null}
              {withEnd ? <span className="text-right font-mono text-[#8E8E8E]" style={{ fontSize: 'inherit' }}>{r.leaseEnd ?? '-'}</span> : null}
            </button>
          ))}
        </div>
        {rows.some((r) => !r.rent && r.asking) ? <p className="mt-1 text-[#8E8E8E]" style={{ fontSize: 11 }}>{fr ? '* loyer demandé' : '* asking rent'}</p> : null}
        {!withRent && rows.length ? <p className="mt-1 text-[#8E8E8E]" style={{ fontSize: 11 }}>{fr ? 'Aucun loyer saisi dans l’état locatif de cet actif.' : 'No rent entered in this asset’s rent roll.'}</p> : null}
      </div>
    </div>
  );
});
